/**
 * HTTP-backed implementation of `BenefitsPerTierService`.
 *
 * Talks to the PartnerProgramApi endpoints under `/api/ProgramConfig`:
 *   - GET    /benefitsPerTier                          → committed flat rows
 *   - GET    /pendingChanges/benefitsPerTier           → all pending per-benefit changes
 *   - PUT    /benefitsPerTier/{benefitName}            → stage Create/Update
 *   - POST   /benefitsPerTier/{benefitName}/rename     → stage Rename
 *   - DELETE /benefitsPerTier/{benefitName}            → stage Delete
 *   - DELETE /pendingChanges/benefitsPerTier/{benefitName} → discard a pending
 *
 * The backend stores benefits as one row per (TierName, Benefit) pair; the
 * UI consumes a per-benefit matrix. This service pivots flat → matrix on
 * read and unpivots matrix → flat on write.
 */

import type { ApiService } from './apiService';
import { ApiError } from './apiService';
import {
  BenefitsPerTierError,
  EMPTY_ELIGIBILITY,
  type Benefit,
  type BenefitsPerTierService,
  type LastUpdatedInfo,
  type PendingBenefitInfo,
} from './benefitsPerTierService';
import {
  TIER_KEYS,
  TIER_LABELS,
  tierKeyFromName,
  type TierKey,
} from '../constants/tiers';

interface BenefitsPerTierRow {
  tierName: string;
  benefit: string;
}

interface PendingBenefitsPerTierChangeRow {
  id: string;
  scopeName: string;
  newScopeName: string | null;
  operation: string;
  payload: BenefitsPerTierRow[] | null;
  createdAt: string;
  createdBy: string;
}

interface LastAppliedInfoRow {
  scopeName: string;
  lastAppliedAt: string;
  lastAppliedBy: string;
}

const slugify = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'benefit';

const normalize = (name: string) => name.trim().toLowerCase();

const eligibilityFromRows = (rows: readonly BenefitsPerTierRow[], benefitName: string): Record<TierKey, boolean> => {
  const eligibility = { ...EMPTY_ELIGIBILITY };
  const target = normalize(benefitName);
  for (const row of rows) {
    if (normalize(row.benefit) !== target) continue;
    const tierKey = tierKeyFromName(row.tierName);
    if (!tierKey) continue;
    eligibility[tierKey] = true;
  }
  return eligibility;
};

export class HttpBenefitsPerTierService implements BenefitsPerTierService {
  private readonly api: ApiService;

  constructor(api: ApiService) {
    this.api = api;
  }

  async list(): Promise<Benefit[]> {
    const [committedRows, pendingRows, lastAppliedRows] = await Promise.all([
      this.api.get<BenefitsPerTierRow[]>('/ProgramConfig/benefitsPerTier'),
      this.api
        .get<PendingBenefitsPerTierChangeRow[] | null>(
          '/ProgramConfig/pendingChanges/benefitsPerTier',
        )
        .catch((err) => {
          console.warn(
            '[HttpBenefitsPerTierService] Failed to load pending changes:',
            err,
          );
          return null;
        }),
      // Last-applied feeds the "Last updated" column. Graceful-degradation:
      // any failure returns an empty list so the table still renders.
      this.api
        .get<LastAppliedInfoRow[]>('/ProgramConfig/lastApplied/benefitsPerTier')
        .catch((err) => {
          console.warn(
            '[HttpBenefitsPerTierService] Failed to load last-applied:',
            err,
          );
          return [] as LastAppliedInfoRow[];
        }),
    ]);

    const committed = (committedRows ?? []).filter((row) => {
      if (!row.benefit?.trim()) return false;
      const tierKey = tierKeyFromName(row.tierName);
      if (!tierKey) {
        console.warn(
          `[HttpBenefitsPerTierService] Ignoring unknown tier "${row.tierName}" for benefit "${row.benefit}".`,
        );
        return false;
      }
      return true;
    });

    const committedOrder: string[] = [];
    const committedNameByKey = new Map<string, string>();
    const committedEligibility = new Map<string, Record<TierKey, boolean>>();
    for (const row of committed) {
      const key = normalize(row.benefit);
      if (!committedNameByKey.has(key)) {
        committedNameByKey.set(key, row.benefit.trim());
        committedEligibility.set(key, { ...EMPTY_ELIGIBILITY });
        committedOrder.push(key);
      }
      const tierKey = tierKeyFromName(row.tierName)!;
      committedEligibility.get(key)![tierKey] = true;
    }

    const pending = pendingRows ?? [];

    type PendingResolved = {
      operation: 'create' | 'update' | 'rename' | 'delete';
      payload: BenefitsPerTierRow[];
      createdAt: string;
      createdBy: string;
      sourceScope: string;
      targetScope: string;
    };
    const pendingByTarget = new Map<string, PendingResolved>();
    const validOperations: ReadonlySet<PendingResolved['operation']> =
      new Set(['create', 'update', 'rename', 'delete']);
    const skippedOps: string[] = [];
    for (const p of pending) {
      const op = p.operation.toLowerCase();
      if (!validOperations.has(op as PendingResolved['operation'])) {
        skippedOps.push(`${p.operation} (${p.scopeName})`);
        continue;
      }
      const typedOp = op as PendingResolved['operation'];
      const target = typedOp === 'rename' && p.newScopeName ? p.newScopeName : p.scopeName;
      pendingByTarget.set(normalize(target), {
        operation: typedOp,
        payload: p.payload ?? [],
        createdAt: p.createdAt,
        createdBy: p.createdBy,
        sourceScope: p.scopeName,
        targetScope: target,
      });
    }
    if (skippedOps.length > 0) {
      console.warn(
        `[HttpBenefitsPerTierService] Ignored ${skippedOps.length} pending change(s) with unknown operation: ${skippedOps.join(', ')}`,
      );
    }

    const result: Benefit[] = [];
    const seenTargets = new Set<string>();

    // Build lookup for the "Last updated" column. Keyed by lowercased benefit
    // name so it matches `normalize()` used elsewhere in this method.
    const lastUpdatedByName = new Map<string, LastUpdatedInfo>();
    for (const row of lastAppliedRows ?? []) {
      lastUpdatedByName.set(normalize(row.scopeName), {
        at: row.lastAppliedAt,
        by: row.lastAppliedBy,
      });
    }

    for (const key of committedOrder) {
      const committedName = committedNameByKey.get(key)!;
      const previousEligibility = committedEligibility.get(key)!;

      const renamedAway = pending.find(
        (p) =>
          p.operation.toLowerCase() === 'rename' &&
          normalize(p.scopeName) === key &&
          p.newScopeName &&
          normalize(p.newScopeName) !== key,
      );
      if (renamedAway) {
        const newName = renamedAway.newScopeName!;
        const targetKey = normalize(newName);
        const renamePayload = renamedAway.payload ?? [];
        const newEligibility = renamePayload.length === 0
          ? { ...previousEligibility }
          : eligibilityFromRows(renamePayload, newName);
        const info: PendingBenefitInfo = {
          operation: 'rename',
          createdAt: renamedAway.createdAt,
          createdBy: renamedAway.createdBy,
          previousName: renamedAway.scopeName,
          previousEligibility,
        };
        result.push({
          id: slugify(newName),
          name: newName,
          eligibility: newEligibility,
          pending: info,
        });
        seenTargets.add(targetKey);
        continue;
      }

      const targetPending = pendingByTarget.get(key);
      if (!targetPending) {
        result.push({
          id: slugify(committedName),
          name: committedName,
          eligibility: previousEligibility,
        });
        seenTargets.add(key);
        continue;
      }

      seenTargets.add(key);
      const { operation, payload, createdAt, createdBy } = targetPending;

      if (operation === 'delete') {
        result.push({
          id: slugify(committedName),
          name: committedName,
          eligibility: previousEligibility,
          pending: { operation, createdAt, createdBy, previousEligibility },
        });
        continue;
      }

      const newEligibility = eligibilityFromRows(payload, committedName);

      const noEffectiveDiff =
        operation === 'update' &&
        TIER_KEYS.every((k) => previousEligibility[k] === newEligibility[k]);
      if (noEffectiveDiff) {
        result.push({
          id: slugify(committedName),
          name: committedName,
          eligibility: previousEligibility,
        });
        continue;
      }
      result.push({
        id: slugify(committedName),
        name: committedName,
        eligibility: newEligibility,
        pending: { operation, createdAt, createdBy, previousEligibility },
      });
    }

    for (const [targetKey, p] of pendingByTarget) {
      if (seenTargets.has(targetKey)) continue;
      const displayName = p.targetScope;

      const info: PendingBenefitInfo = {
        operation: p.operation,
        createdAt: p.createdAt,
        createdBy: p.createdBy,
      };
      let newEligibility = eligibilityFromRows(p.payload, displayName);
      if (p.operation === 'rename') {
        info.previousName = p.sourceScope;
        const sourceKey = normalize(p.sourceScope);
        const prevEl = committedEligibility.get(sourceKey);
        info.previousEligibility = prevEl;

        if (p.payload.length === 0 && prevEl) {
          newEligibility = { ...prevEl };
        }
      }
      result.push({
        id: slugify(displayName),
        name: displayName,
        eligibility: newEligibility,
        pending: info,
      });
    }

    // Attach last-updated metadata. For renamed rows we fall back to the
    // pre-rename name because the partition key in Cosmos still reflects the
    // original name until another change is applied to the new one.
    for (const benefit of result) {
      const key = normalize(benefit.name);
      const previousKey =
        benefit.pending?.operation === 'rename' && benefit.pending.previousName
          ? normalize(benefit.pending.previousName)
          : null;
      const info =
        lastUpdatedByName.get(key) ??
        (previousKey ? lastUpdatedByName.get(previousKey) : undefined);
      if (info) {
        benefit.lastUpdated = { ...info };
      }
    }

    return result;
  }

  async upsertBenefit(name: string, eligibility: Record<TierKey, boolean>): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new BenefitsPerTierError('EMPTY_NAME', 'Benefit name is required.');
    }
    const items: BenefitsPerTierRow[] = [];
    for (const key of TIER_KEYS) {
      if (eligibility[key]) {
        items.push({ tierName: TIER_LABELS[key], benefit: trimmed });
      }
    }

    try {
      await this.api.put<void>(
        `/ProgramConfig/benefitsPerTier/${encodeURIComponent(trimmed)}`,
        { items },
      );
    } catch (err) {
      throw this.toDomainError(err);
    }
  }

  async renameBenefit(currentName: string, newName: string): Promise<void> {
    const current = currentName.trim();
    const next = newName.trim();
    if (!current || !next) {
      throw new BenefitsPerTierError('EMPTY_NAME', 'Benefit name is required.');
    }

    try {
      await this.api.post<void>(
        `/ProgramConfig/benefitsPerTier/${encodeURIComponent(current)}/rename`,
        { newName: next },
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        throw new BenefitsPerTierError(
          'DUPLICATE_NAME',
          this.extractServerMessage(err) ?? `A benefit named "${next}" already exists.`,
        );
      }
      throw this.toDomainError(err);
    }
  }

  async deleteBenefit(name: string): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new BenefitsPerTierError('EMPTY_NAME', 'Benefit name is required.');
    }
    try {
      await this.api.delete<void>(
        `/ProgramConfig/benefitsPerTier/${encodeURIComponent(trimmed)}`,
      );
    } catch (err) {
      throw this.toDomainError(err);
    }
  }

  async cancelPendingForBenefit(name: string): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) return;
    await this.api.delete<void>(
      `/ProgramConfig/pendingChanges/benefitsPerTier/${encodeURIComponent(trimmed)}`,
    );
  }

  private extractServerMessage(err: ApiError): string | undefined {
    if (
      err.problemDetails &&
      typeof err.problemDetails === 'object' &&
      'error' in err.problemDetails
    ) {
      const message = (err.problemDetails as { error?: unknown }).error;
      if (typeof message === 'string') return message;
    }
    return undefined;
  }

  private toDomainError(err: unknown): Error {
    if (err instanceof ApiError && err.status === 400) {
      return new BenefitsPerTierError(
        'SERVER_ERROR',
        this.extractServerMessage(err) ?? err.message,
      );
    }
    return err instanceof Error ? err : new Error(String(err));
  }
}
